// Data conversion system for spreadsheet fields
// Based on Java implementation from SystemField.java and FHQ4ServiceImpl.java

export interface DataConverter {
  actualValue: string | number;
  displayValue: string;
}

// Field IDs from Java SystemField.java
export const FIELD_IDS = {
  SEX: 2,
  GENDER: 148,
  DECEASED: 6,
  TWIN_STATUS: 19,
  TWIN_RELATIONSHIP: 20,
  DEGREE_3: 34,
  PROBAND_STATUS: 5,
  ADOPTED: 11,
  NO_ISSUE: 14,
  SAB_STATUS: 12,
  GENDER_UNKNOWN: 10,
  AUDIT_STATUS: 30,
  SAMPLE_AUDIT_STATUS: 31,
  INVITE_STATUS: 88,
  GENDER_IDENTITY: 149,
  LOCK_STATUS: 150
} as const;

// Date formatting constants
export const DATE_FORMAT_CONSTANTS = {
INPUT_FORMAT: 'MM/DD/YYYY',  // Format for date input fields
DISPLAY_FORMAT: 'MM/DD/YYYY', // Format for display
API_FORMAT: 'YYYY-MM-DD',     // Format for API calls
} as const;

// Gender field constants - based on Java mapGenderIntToString and mapGenderStringToInt
export const GENDER_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Woman' },
  { actualValue: '1', displayValue: 'Man' },
  { actualValue: '2', displayValue: 'Unknown' },
  { actualValue: '3', displayValue: 'Transgender woman' },
  { actualValue: '4', displayValue: 'Transgender man' },
  { actualValue: '5', displayValue: 'Non-binary' },
  { actualValue: '6', displayValue: 'Self-described' },
];

// Sex field constants - based on Java mapSexIntToString and mapSexStringToInt
export const SEX_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Female' },
  { actualValue: '1', displayValue: 'Male' },
  { actualValue: '2', displayValue: 'Unknown' },
];

// Deceased field constants - based on Java mapDeceasedIntToString and mapDeceasedStringToInt
export const DECEASED_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Alive' },
  { actualValue: '1', displayValue: 'Deceased' },
];

// Twin relationship constants - based on Java mapTwinRelIntToString and mapTwinRelStringToInt
export const TWIN_RELATIONSHIP_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Dizygotic' },
  { actualValue: '1', displayValue: 'Monozygotic' },
  { actualValue: '2', displayValue: 'Unknown' },
];

// Degree 3 constants - based on Java mapDegree3IntToString and mapDegree3StringToInt
export const DEGREE_3_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'None' },
  { actualValue: '1', displayValue: 'Mat/Maternal' },
  { actualValue: '2', displayValue: 'Pat/Paternal' },
  { actualValue: '3', displayValue: 'Both' },
  { actualValue: '4', displayValue: 'Con/Consanguineous' },
];

// Yes/No constants for boolean fields
export const YES_NO_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'No' },
  { actualValue: '1', displayValue: 'Yes' },
  { actualValue: '2', displayValue: 'Unknown' },
];

// Yes/No constants for boolean fields
export const ADOPTED_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'No' },
  { actualValue: '1', displayValue: 'In' },
  { actualValue: '2', displayValue: 'Out' },
];

// Yes/No constants for boolean fields
export const NO_ISSUE_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'No' },
  { actualValue: '1', displayValue: 'Yes' },
  { actualValue: '2', displayValue: 'Infertile' },
];

// Off/On constants for boolean fields
export const AUDIT_STATUS_CONSTANTS: DataConverter[] = [
  { actualValue: '0', displayValue: 'Off' },
  { actualValue: '1', displayValue: 'On' },
];

// Map of field names to their constants
// Supports multiple variations (with/without spaces, different cases)
export const FIELD_CONSTANTS_MAP: Record<string, DataConverter[]> = {
  'gender': GENDER_CONSTANTS,
  'sex': SEX_CONSTANTS,
  'deceased': DECEASED_CONSTANTS,
  'deceasedstatus': DECEASED_CONSTANTS,
  'twinrelationship': TWIN_RELATIONSHIP_CONSTANTS,
  'degree3': DEGREE_3_CONSTANTS,
  'proband': YES_NO_CONSTANTS,
  'probandstatus': YES_NO_CONSTANTS,
  'adopted': ADOPTED_CONSTANTS,
  'noissue': NO_ISSUE_CONSTANTS,
  'sabstatus': YES_NO_CONSTANTS,
  'sab': YES_NO_CONSTANTS,
  'genderunknown': YES_NO_CONSTANTS,
  'sexunknown': YES_NO_CONSTANTS,
  'twinstatus': YES_NO_CONSTANTS,
  'twin': YES_NO_CONSTANTS,
  'auditstatus': AUDIT_STATUS_CONSTANTS,
  'audit': AUDIT_STATUS_CONSTANTS,
  'sampleauditstatus': AUDIT_STATUS_CONSTANTS,
  'sampleaudit': AUDIT_STATUS_CONSTANTS,
  'invitestatus': YES_NO_CONSTANTS,
  'lockstatus': YES_NO_CONSTANTS,
};

// Map of field IDs to their constants (for system fields)
export const FIELD_ID_CONSTANTS_MAP: Record<number, DataConverter[]> = {
  [FIELD_IDS.SEX]: SEX_CONSTANTS,
  [FIELD_IDS.GENDER]: GENDER_CONSTANTS,
  [FIELD_IDS.DECEASED]: DECEASED_CONSTANTS,
  [FIELD_IDS.TWIN_RELATIONSHIP]: TWIN_RELATIONSHIP_CONSTANTS,
  [FIELD_IDS.DEGREE_3]: DEGREE_3_CONSTANTS,
  [FIELD_IDS.PROBAND_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.ADOPTED]: ADOPTED_CONSTANTS,
  [FIELD_IDS.NO_ISSUE]: NO_ISSUE_CONSTANTS,
  [FIELD_IDS.SAB_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.GENDER_UNKNOWN]: YES_NO_CONSTANTS,
  [FIELD_IDS.TWIN_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.AUDIT_STATUS]: AUDIT_STATUS_CONSTANTS,
  [FIELD_IDS.SAMPLE_AUDIT_STATUS]: AUDIT_STATUS_CONSTANTS,
  [FIELD_IDS.INVITE_STATUS]: YES_NO_CONSTANTS,
  [FIELD_IDS.LOCK_STATUS]: YES_NO_CONSTANTS,
};

// Helper function to get constants for a field by name
export const getFieldConstants = (fieldName: string): DataConverter[] | undefined => {
  if (!fieldName) return undefined;
  const normalizedFieldName = fieldName.toLowerCase().replace(/\s+/g, '');
  return FIELD_CONSTANTS_MAP[normalizedFieldName];
};

// Helper function to get constants for a field by ID
export const getFieldConstantsById = (fieldId: number): DataConverter[] | undefined => {
  return FIELD_ID_CONSTANTS_MAP[fieldId];
};

// Helper function to convert raw value to display value (for UI display)
export const convertValueToDisplay = (fieldName: string, rawValue: string | number): string => {
  const constants = getFieldConstants(fieldName);
  if (!constants) return String(rawValue);

  const constant = constants.find(c => c.actualValue === String(rawValue));
  return constant?.displayValue || String(rawValue);
};

// Helper function to convert display value to raw value (for API payloads)
export const convertDisplayToValue = (fieldName: string, displayValue: string): string | number => {
  const constants = getFieldConstants(fieldName);
  if (!constants) return displayValue;

  const constant = constants.find(c => c.displayValue === displayValue);
  return constant?.actualValue || displayValue;
};

// Date conversion functions
export const formatDateForInput = (dateValue: string | number | Date): string => {
if (!dateValue) return '';

try {
  // Accept multiple input formats including 2025/10/08
  const INPUT_FORMATS = [
    'YYYY/MM/DD', 'MM/DD/YYYY', 'DD/MM/YYYY',
    'YYYY-MM-DD', 'MM-DD-YYYY', 'DD-MM-YYYY'
  ];
  // Normalize to moment first
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const momentLib = require('moment');
  let m = momentLib.invalid();

  if (typeof dateValue === 'string') {
      // String parsing - parse in UTC to avoid timezone shifts
    m = momentLib.utc(dateValue, INPUT_FORMATS, true);
      if (!m.isValid()) {
        // Fallback: try parsing as UTC date string
        m = momentLib.utc(dateValue);
      }
    } else if (dateValue instanceof Date) {
      // Date object - extract date components using UTC to avoid timezone shifts
      // This ensures the same date is displayed regardless of user's timezone
      m = momentLib.utc([dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate()]);
    } else if (typeof dateValue === 'number') {
      // Number (timestamp) - parse as UTC to avoid timezone conversion
      m = momentLib.utc(dateValue);
  }

  if (!m.isValid()) return String(dateValue);
    // Format using UTC to ensure consistent date display across timezones
  return m.format('MM/DD/YYYY');
} catch (error) {
  console.warn('Failed to format date:', dateValue, error);
  return String(dateValue);
}
};

export const formatDateForDisplay = (dateValue: string | number | Date): string => {
return formatDateForInput(dateValue); // Same format for display
};

export const formatDateForApi = (dateValue: string): string => {
if (!dateValue) return '';

try {
  const INPUT_FORMATS = [
    'MM/DD/YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY',
    'YYYY-MM-DD', 'MM-DD-YYYY', 'DD-MM-YYYY'
  ];
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const momentLib = require('moment');
  let m = momentLib(dateValue, INPUT_FORMATS, true);
  if (!m.isValid()) m = momentLib(dateValue);
  return m.isValid() ? m.format('YYYY-MM-DD') : dateValue;
} catch (error) {
  console.warn('Failed to format date for API:', dateValue, error);
  return dateValue;
}
};

// Main data converter class (similar to Java approach)
export class DataConverter {
/**
 * Convert raw data to display format for UI
 * @param fieldName - The field name (e.g., 'gender', 'deceased')
 * @param rawValue - The raw value from API/database
 * @returns Display value for UI
 */
static toDisplay(fieldName: string, rawValue: string | number): string {
  return convertValueToDisplay(fieldName, rawValue);
}

/**
 * Convert display value back to raw format for API payloads
 * @param fieldName - The field name (e.g., 'gender', 'deceased')
 * @param displayValue - The display value from UI
 * @returns Raw value for API
 */
static toApi(fieldName: string, displayValue: string): string | number {
  return convertDisplayToValue(fieldName, displayValue);
}

/**
 * Convert by field ID (for system fields)
 * @param fieldId - The field ID from FIELD_IDS
 * @param rawValue - The raw value from API/database
 * @returns Display value for UI
 */
static toDisplayById(fieldId: number, rawValue: string | number): string {
  const constants = getFieldConstantsById(fieldId);
  if (!constants) return String(rawValue);

  const constant = constants.find((c) => c.actualValue === String(rawValue));
  return constant?.displayValue || String(rawValue);
}

/**
 * Convert by field ID (for system fields)
 * @param fieldId - The field ID from FIELD_IDS
 * @param displayValue - The display value from UI
 * @returns Raw value for API
 */
static toApiById(fieldId: number, displayValue: string): string | number {
  const constants = getFieldConstantsById(fieldId);
  if (!constants) return displayValue;

  const constant = constants.find((c) => c.displayValue === displayValue);
  return constant?.actualValue || displayValue;
}

/**
 * Convert date value to MM/DD/YYYY format for input fields
 * @param dateValue - The date value (timestamp, string, or Date object)
 * @returns Formatted date string MM/DD/YYYY
 */
static formatDateForInput(dateValue: string | number | Date): string {
  return formatDateForInput(dateValue);
}

/**
 * Convert date value to MM/DD/YYYY format for display
 * @param dateValue - The date value (timestamp, string, or Date object)
 * @returns Formatted date string MM/DD/YYYY
 */
static formatDateForDisplay(dateValue: string | number | Date): string {
  return formatDateForDisplay(dateValue);
}

/**
 * Convert MM/DD/YYYY date string to YYYY-MM-DD for API calls
 * @param dateValue - The date string in MM/DD/YYYY format
 * @returns Formatted date string YYYY-MM-DD
 */
static formatDateForApi(dateValue: string): string {
  return formatDateForApi(dateValue);
}

/**
 * Convert an entire row of data for display
 * @param row - The data row
 * @param columns - The column definitions
 * @returns Row with converted display values
 */
//@ts-ignore  //WILL BE FIXED LATER
static convertRowForDisplay(row: any, columns: any[]): any {
  const convertedRow = { ...row };

  columns.forEach((column) => {
    if (column.colName && row[column.accessorKey || column.colName]) {
      const rawValue = row[column.accessorKey || column.colName];
      convertedRow[column.accessorKey || column.colName] = this.toDisplay(
        column.colName,
        rawValue
      );
    }
  });

  return convertedRow;
}

/**
 * Convert an entire row of data for API payload
 * @param row - The data row with display values
 * @param columns - The column definitions
 * @returns Row with converted raw values for API
 */
//@ts-ignore  //WILL BE FIXED LATER
static convertRowForApi(row: any, columns: any[]): any {
  const convertedRow = { ...row };

  columns.forEach((column) => {
    if (column.colName && row[column.accessorKey || column.colName]) {
      const displayValue = row[column.accessorKey || column.colName];
      convertedRow[column.accessorKey || column.colName] = this.toApi(
        column.colName,
        displayValue
      );
    }
  });

  return convertedRow;
}

/**
 * Get dropdown options for a field (for filter dropdowns)
 * @param fieldName - The field name
 * @returns Array of options with actualValue and displayValue
 */
static getDropdownOptions(fieldName: string): DataConverter[] | undefined {
  return getFieldConstants(fieldName);
}

/**
 * Get dropdown options for a field by ID (for system fields)
 * @param fieldId - The field ID
 * @returns Array of options with actualValue and displayValue
 */
static getDropdownOptionsById(fieldId: number): DataConverter[] | undefined {
  return getFieldConstantsById(fieldId);
}

/**
 * Check if a field has conversion constants
 * @param fieldName - The field name
 * @returns True if field has conversion constants
 */
static hasConversion(fieldName: string): boolean {
  return !!getFieldConstants(fieldName);
}

/**
 * Check if a field ID has conversion constants
 * @param fieldId - The field ID
 * @returns True if field has conversion constants
 */
static hasConversionById(fieldId: number): boolean {
  return !!getFieldConstantsById(fieldId);
}
}

/**
* Helper function to get field type name based on type ID
*/
export const getFieldTypeName = (typeId: number): string => {
  const typeMap: { [key: number]: string } = {
      1: 'Text',
      2: 'Date',
      3: 'Number',
      4: 'Checkbox',
      5: 'Dropdown',
      6: 'Image',
      7: 'LookupTable',
      8: 'Table',
      9: 'Computed',
      11: 'Summary',
      13: 'TableSummary',
      14: 'Hyperlink',
      30: 'Integer',
      [-100]: 'System'
  };

  return typeMap[typeId] || 'Unknown';
};
export const BLANK_DROPDOWN_LABEL = "\u00A0";
const prependBlankRowToLookupValues = (
  valuesRecord: Record<string, any[]>
): Record<string, any[]> => {
  const columnKeys = Object.keys(valuesRecord || {});
  if (columnKeys.length === 0) {
    return valuesRecord;
  }

  const alreadyHasBlank = columnKeys.every((key) => {
    const column = valuesRecord[key];
    if (!Array.isArray(column) || column.length === 0) {
      return true;
    }
    const firstValue = column[0];
    return firstValue === "" || firstValue === undefined || firstValue === null;
  });

  if (alreadyHasBlank) {
    return valuesRecord;
  }

  return columnKeys.reduce((acc, key) => {
    const columnValues = Array.isArray(valuesRecord[key])
    ? [...valuesRecord[key]!]
    : [];
    acc[key] = ["", ...columnValues];
    return acc;
  }, {} as Record<string, any[]>);
};

/**
 * Prepends a blank option (value/label empty string) to dropdown options
 * unless an empty entry already exists.
 */
export const addBlankOptionToDropdownOptions = <
  T extends { value: string | number; label: string | number }
>(
  options: T[] = []
): T[] => {
  const normalized = options ?? [];

  const hasBlank = normalized.some(
    (opt) => opt.value === "" || opt.value === null || opt.value === undefined
  );

  if (hasBlank) {
    return normalized as T[];
  }

  return [{ value: "", label: BLANK_DROPDOWN_LABEL } as T, ...normalized];
};

export const normalizeLookupValues = (lookupValues: any) => {
  if (!lookupValues) {
    return {};
  }

  // Case 1: Already in object format — clone and ensure blank row
  if (!Array.isArray(lookupValues)) {
    const normalizedRecord = Object.keys(lookupValues).reduce(
      (acc, key) => {
        const columnValues = Array.isArray(lookupValues[key])
          ? [...lookupValues[key]]
          : [];
        acc[key] = columnValues;
        return acc;
      },
      {} as Record<string, any[]>
    );
    return prependBlankRowToLookupValues(normalizedRecord);
  }

  // Case 2: Array format — convert to object then ensure blank row
  const record = lookupValues.reduce((acc, item) => {
    const columnValues = Array.isArray(item.values) ? [...item.values] : [];
    acc[item.key] = columnValues;
    return acc;
  }, {} as Record<string, any[]>);

  return prependBlankRowToLookupValues(record);
};
/**
 * Makes a deep copy of smartColumns and removes unwanted fields
 * (dropdown/lookup values + modulo) before sending to API.
 *
 * Keeps original data untouched.
 *
 * @param columns - smartColumns array
 * @returns cleaned smartColumns array for API
 */
export const cleanSmartColumnsForApi = (columns: any[]): any[] => {
  if (!Array.isArray(columns)) {
    return [];
  }

  return columns.map((col: any) => {
    // clone object + field + parentField to avoid mutating original
    const cloned: any = {
      ...col,
      field: col.field ? { ...col.field } : undefined,
      parentField: col.parentField ? { ...col.parentField } : undefined,
    };

    if (cloned.field) {
      delete cloned.field.dropdownValues;
      delete cloned.field.lookupValues;
      delete cloned.field.options;
      delete cloned.field.dropdownOptions;
      delete cloned.field.editable;
      // Convert id to fieldid, then remove id
      if (cloned.field.id !== undefined && cloned.field.fieldid === undefined) {
        cloned.field.fieldid = cloned.field.id;
      }
      delete cloned.field.id;
    }

    if (cloned.parentField) {
      delete cloned.parentField.dropdownValues;
      delete cloned.parentField.lookupValues;
      delete cloned.parentField.options;
      delete cloned.parentField.dropdownOptions;
      delete cloned.parentField.editable;
      // Convert id to fieldid, then remove id
      if (cloned.parentField.id !== undefined && cloned.parentField.fieldid === undefined) {
        cloned.parentField.fieldid = cloned.parentField.id;
      }
      delete cloned.parentField.id;
      delete cloned.parentField.internalId;
      delete cloned.parentField.fullPath;
      delete cloned.parentField.context;
      delete cloned.parentField.name;
      delete cloned.parentField.parentField;  // nested parentfield inside parentfield
    }

    // remove any column-level dropdown caches
    delete cloned.dropdownOptions;
    delete cloned.dataDropdownOptions;
    // delete cloned.editable;
    //
    // remove modulo and return rest
    const { modulo, ...rest } = cloned;
    return rest;
  });
};

/**
 * Enriches queryModel's whereClauses with dropdown/options/lookup values from smartColumns.
 * Matches fields by fieldid and optionally parentField to handle duplicate fieldids.
 * Creates a new object without mutating the original.
 */
export const hydrateQueryModelFromSmartColumns = (
  queryModel: any,
  smartColumns: any[]
): any => {
  if (!queryModel || typeof queryModel !== 'object') {
    return queryModel || { whereClauses: [] };
  }

  if (!Array.isArray(smartColumns) || smartColumns.length === 0) {
    return queryModel;
  }

  // Create a map of smartColumns by fieldid for quick lookup
  // Since fieldid can be same, we also consider parentField for matching
  const smartColumnMap = new Map<string, any>();
  
  smartColumns.forEach((smartCol: any) => {
    if (!smartCol?.field?.fieldid) return;
    
    const fieldId = smartCol.field.fieldid;
    const parentFieldId = smartCol.parentField?.fieldid || smartCol.parentField?.fieldid || 0;
    const key = `${fieldId}_${parentFieldId}`;
    
    // Store the smartColumn with the most complete field data
    if (!smartColumnMap.has(key) || smartCol.field.dropdownValues || smartCol.field.lookupValues || smartCol.field.options) {
      smartColumnMap.set(key, smartCol);
    }
  });

  // Create enriched queryModel
  const enriched: any = {
    ...queryModel,
    whereClauses: Array.isArray(queryModel.whereClauses)
      ? queryModel.whereClauses.map((clause: any) => {
          // Clone clause + field + parentField to avoid mutating original
          const enrichedClause: any = {
            ...clause,
            field: clause.field ? { ...clause.field } : undefined,
            parentField: clause.parentField ? { ...clause.parentField } : undefined,
          };

          // Enrich field with dropdown/options/lookup and core metadata from smartColumns
          if (enrichedClause.field && enrichedClause.field.fieldid) {
            const clauseFieldId = enrichedClause.field.fieldid;
            const clauseParentFieldId = enrichedClause.parentField?.fieldid || enrichedClause.parentField?.fieldId || 0;
            const key = `${clauseFieldId}_${clauseParentFieldId}`;
            
            const matchingSmartCol = smartColumnMap.get(key);
            
            // Try to find by fieldid only if no match with parentField
            let smartCol = matchingSmartCol;
            if (!smartCol) {
              // Fallback: find first smartColumn with matching fieldid (ignore parentField)
              smartCol = smartColumns.find(
                (col: any) => col?.field?.fieldid === clauseFieldId
              );
            }

            if (smartCol?.field) {
              // Core metadata (only fill if missing on clause)
              if (!enrichedClause.field.fieldname && (smartCol.field.fieldname || smartCol.field.name)) {
                enrichedClause.field.fieldname = smartCol.field.fieldname || smartCol.field.name;
              }
              if (!enrichedClause.field.name && (smartCol.field.name || smartCol.field.fieldname)) {
                enrichedClause.field.name = smartCol.field.name || smartCol.field.fieldname;
              }
              if (enrichedClause.field.type === undefined && (smartCol.field.fieldtype?.typeid !== undefined || smartCol.field.type !== undefined)) {
                enrichedClause.field.type = smartCol.field.fieldtype?.typeid ?? smartCol.field.type;
              }
              if (enrichedClause.field.system === undefined && smartCol.field.system !== undefined) {
                enrichedClause.field.system = smartCol.field.system;
              }
              if (!enrichedClause.field.imageURL && smartCol.field.imageURL) {
                enrichedClause.field.imageURL = smartCol.field.imageURL;
              }

              // Enrich with dropdown values
              if (smartCol.field.dropdownValues && !enrichedClause.field.dropdownValues) {
                enrichedClause.field.dropdownValues = [...smartCol.field.dropdownValues];
              }

              // Enrich with options
              if (smartCol.field.options && !enrichedClause.field.options) {
                enrichedClause.field.options = Array.isArray(smartCol.field.options)
                  ? [...smartCol.field.options]
                  : smartCol.field.options;
              }

              // Enrich with lookup values
              if (smartCol.field.lookupValues && !enrichedClause.field.lookupValues) {
                enrichedClause.field.lookupValues = typeof smartCol.field.lookupValues === 'object'
                  ? { ...smartCol.field.lookupValues }
                  : smartCol.field.lookupValues;
              }
            }
          }

          // Enrich parentField with dropdown/options/lookup and core metadata from smartColumns
          if (enrichedClause.parentField) {
            const parentFieldId = enrichedClause.parentField.fieldid || enrichedClause.parentField.fieldId;
            if (parentFieldId) {
              const clauseFieldId = enrichedClause.field?.fieldid || 0;
              
              // First try to find exact match: field matches AND parentField matches
              let smartCol = smartColumnMap.get(`${clauseFieldId}_${parentFieldId}`);
              
              // If exact match found but its parentField doesn't match, look for another
              if (!smartCol || smartCol.parentField?.fieldid !== parentFieldId) {
                // Find smartColumn where both field and parentField match
                smartCol = smartColumns.find(
                  (col: any) => 
                    col?.field?.fieldid === clauseFieldId &&
                    col?.parentField?.fieldid === parentFieldId
                );
              }

              // If still no match, find any smartColumn with matching parentField
              if (!smartCol || smartCol.parentField?.fieldid !== parentFieldId) {
                smartCol = smartColumns.find(
                  (col: any) => col?.parentField?.fieldid === parentFieldId
                );
              }

              if (smartCol?.parentField && smartCol.parentField.fieldid === parentFieldId) {
                // Core metadata for parentField
                if (!enrichedClause.parentField.fieldname && (smartCol.parentField.fieldname || smartCol.parentField.fieldName)) {
                  enrichedClause.parentField.fieldname = smartCol.parentField.fieldname || smartCol.parentField.fieldName;
                }
                if (!enrichedClause.parentField.name && (smartCol.parentField.fieldName || smartCol.parentField.fieldname)) {
                  enrichedClause.parentField.name = smartCol.parentField.fieldName || smartCol.parentField.fieldname;
                }
                if (enrichedClause.parentField.type === undefined && (smartCol.parentField.fieldtype?.typeid !== undefined || smartCol.parentField.type !== undefined)) {
                  enrichedClause.parentField.type = smartCol.parentField.fieldtype?.typeid ?? smartCol.parentField.type;
                }
                if (enrichedClause.parentField.system === undefined && smartCol.parentField.system !== undefined) {
                  enrichedClause.parentField.system = smartCol.parentField.system;
                }
                if (!enrichedClause.parentField.imageURL && smartCol.parentField.imageURL) {
                  enrichedClause.parentField.imageURL = smartCol.parentField.imageURL;
                }

                // Enrich parentField with dropdown values
                if (smartCol.parentField.dropdownValues && !enrichedClause.parentField.dropdownValues) {
                  enrichedClause.parentField.dropdownValues = [...smartCol.parentField.dropdownValues];
                }

                // Enrich parentField with options
                if (smartCol.parentField.options && !enrichedClause.parentField.options) {
                  enrichedClause.parentField.options = Array.isArray(smartCol.parentField.options)
                    ? [...smartCol.parentField.options]
                    : smartCol.parentField.options;
                }

                // Enrich parentField with lookup values
                if (smartCol.parentField.lookupValues && !enrichedClause.parentField.lookupValues) {
                  enrichedClause.parentField.lookupValues = typeof smartCol.parentField.lookupValues === 'object'
                    ? { ...smartCol.parentField.lookupValues }
                    : smartCol.parentField.lookupValues;
                }
              }
            }
          }

          return enrichedClause;
        })
      : [],
  };

  return enriched;
};

//Clean queryModel before API call by removing dropdown/options/lookup data.
export const cleanQueryModelForApi = (queryModel: any): any => {
  if (!queryModel || typeof queryModel !== 'object') {
    return queryModel || { whereClauses: [] };
  }
  

  // Create a completely new object to avoid mutating original
  const cleaned: any = {
    ...queryModel,
    whereClauses: Array.isArray(queryModel.whereClauses) 
      ? queryModel.whereClauses.map((clause: any) => {
          const cleanedClause: any = { ...clause };
          
          if (clause.field) {
            cleanedClause.field = { ...clause.field };
            delete cleanedClause.field.dropdownValues;
            delete cleanedClause.field.options;
            delete cleanedClause.field.lookupValues;
          }

          if (clause.parentField) {
            cleanedClause.parentField = { ...clause.parentField };
            delete cleanedClause.parentField.dropdownValues;
            delete cleanedClause.parentField.options;
            delete cleanedClause.parentField.lookupValues;
          }
          
          return cleanedClause;
        })
      : [],
  };
  
  return cleaned;
};

// Export the main converter for easy access
export default DataConverter;